// src/pages/provider/UserActivities.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { logService } from '../../services/apiServices';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showInfoToast } from '../../utils/toastUtils';

const UserActivities = () => {
  const { userId } = useParams();
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  // Fetch user activities on component mount
  useEffect(() => {
    fetchUserActivities();
  }, [userId]);

  const fetchUserActivities = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch activities for the specific user
      const response = await logService.getLogs({ userId: userId });
      
      if (response.success) {
        setActivities(response.data || []);
        
        // Try to get user info from the first activity
        if (response.data && response.data.length > 0) {
          const firstActivity = response.data[0];
          setUser({
            id: userId,
            name: firstActivity.userName || firstActivity.userId || 'Utilisateur inconnu'
          });
        }
      } else {
        throw new Error(response.message || 'Failed to fetch user activities');
      }
    } catch (error) {
      console.error('Error fetching user activities:', error);
      setError('Erreur lors du chargement des activités de l\'utilisateur. Veuillez réessayer plus tard.');
      
      // Use mock data in development for testing
      if (process.env.NODE_ENV === 'development') {
        setActivities([
          { id: '1', timestamp: new Date(Date.now() - 30 * 60000).toISOString(), userId: userId, userName: 'John Doe', actionType: 'USER_LOGIN', resourceType: 'user', ipAddress: '192.168.1.100', details: {} },
          { id: '2', timestamp: new Date(Date.now() - 120 * 60000).toISOString(), userId: userId, userName: 'John Doe', actionType: 'ANALYSIS_CREATE', resourceType: 'analysis', ipAddress: '192.168.1.101', details: {} },
          { id: '3', timestamp: new Date(Date.now() - 240 * 60000).toISOString(), userId: userId, userName: 'John Doe', actionType: 'USER_UPDATE', resourceType: 'user', ipAddress: '192.168.1.102', details: {} }
        ]);
        
        setUser({
          id: userId,
          name: 'John Doe'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to get the color of the badge based on action type
  const getActionColor = (actionType) => {
    if (actionType.includes('LOGIN')) return 'bg-blue-100 text-blue-800';
    if (actionType.includes('CREATE')) return 'bg-green-100 text-green-800';
    if (actionType.includes('UPDATE')) return 'bg-yellow-100 text-yellow-800';
    if (actionType.includes('DELETE')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Helper to display activity details
  const showActivityDetails = (details) => {
    if (!details || Object.keys(details).length === 0) {
      showInfoToast('Aucun détail disponible pour cette activité.');
      return;
    }

    // Format the details for display
    const formattedDetails = JSON.stringify(details, null, 2);
    showInfoToast(formattedDetails);
  };

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="font-medium">Erreur</p>
          <p>{error}</p>
          <button 
            onClick={fetchUserActivities} 
            className="mt-2 bg-red-100 px-3 py-1 rounded-md hover:bg-red-200"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">
        Logs d'Activité de l'Utilisateur{user ? `: ${user.name}` : ''}
      </h1>
      
      {activities.length === 0 ? (
        <div className="bg-white p-6 rounded-lg shadow-md text-center text-gray-500">
          Aucune activité trouvée pour cet utilisateur.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ressource
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Détails
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activities.map((activity) => (
                <tr key={activity.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {new Date(activity.timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getActionColor(
                        activity.actionType
                      )}`}
                    >
                      {activity.actionType}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {activity.resourceType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {activity.ipAddress}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => showActivityDetails(activity.details)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Voir détails
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserActivities;